import { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { MessageCircle, UserPlus, Check, Clock, PlayCircle, ThumbsUp, Award, Leaf, Sparkles, CheckCircle2 } from "lucide-react";
import AvatarTiered from "@/components/AvatarTiered";

const resolve = (u) => (u?.startsWith("http") ? u : `${BACKEND_URL}${u}`);

const TIER_META = {
  sprout:    { label: "🌱 Sprout",    at: 50 },
  grove:     { label: "🌿 Grove",     at: 100 },
  canopy:    { label: "🌳 Canopy",    at: 200 },
  forest:    { label: "🌲 Forest",    at: 500 },
  ecosystem: { label: "🌍 Ecosystem", at: 1000 },
};

export default function Profile() {
  const { userId } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/users/${userId}/profile`);
      setData(data);
      setErr(null);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load profile");
    }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  const sendFriendRequest = async () => {
    try {
      await api.post("/friends/request", { target_id: userId });
      toast.success("Friend request sent");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (err) return <div className="mx-auto max-w-4xl px-6 py-24 text-muted-foreground">{err}</div>;
  if (!data) return <div className="mx-auto max-w-4xl px-6 py-24 text-muted-foreground">Loading…</div>;

  const { user: u, tier, stats, proposals, videos, friendship, is_self } = data;
  const tierMeta = tier.key ? TIER_META[tier.key] : null;
  const nextAt = tier.next_at;
  const progress = nextAt ? Math.min(100, Math.round((stats.total_upvotes / nextAt) * 100)) : 100;

  return (
    <div className="mx-auto max-w-6xl px-6 py-16">
      {/* Hero */}
      <div className="grid gap-8 md:grid-cols-[auto_1fr] items-start">
        <div className="flex flex-col items-center gap-3">
          <AvatarTiered user={u} size="xl" testId="profile-avatar" />
          {tierMeta && (
            <div data-testid="profile-tier-badge" className="border border-primary/60 px-3 py-1 text-xs uppercase tracking-widest text-primary">
              {tierMeta.label}
            </div>
          )}
        </div>
        <div className="min-w-0">
          <div className="mono text-xs text-primary">// profile</div>
          <div className="mt-2 flex flex-wrap items-baseline gap-3">
            <h1 className="text-5xl font-black tracking-tighter" data-testid="profile-name">{u.display_name}</h1>
            <span className="border border-border px-2 py-0.5 text-[10px] uppercase tracking-widest text-muted-foreground">{u.role}</span>
            {u.qualified && <span className="border border-primary px-2 py-0.5 text-[10px] uppercase tracking-widest text-primary">Verified</span>}
          </div>
          {u.credentials && (
            <p className="mt-3 max-w-2xl text-muted-foreground text-sm leading-relaxed">{u.credentials}</p>
          )}

          {/* Actions */}
          <div className="mt-5 flex flex-wrap gap-2">
            {is_self ? (
              <Link to="/settings"><Button data-testid="profile-edit-btn" className="rounded-none">Edit profile</Button></Link>
            ) : user ? (
              <>
                <Button
                  data-testid="profile-msg-btn"
                  onClick={() => nav(`/messages?to=${u.id}&name=${encodeURIComponent(u.display_name)}`)}
                  className="rounded-none bg-primary text-primary-foreground hover:bg-primary/90"
                >
                  <MessageCircle className="mr-2 h-4 w-4" />Message
                </Button>
                {friendship === "accepted" ? (
                  <Button variant="outline" disabled className="rounded-none"><Check className="mr-2 h-3 w-3" />Friends</Button>
                ) : friendship === "pending" ? (
                  <Button variant="outline" disabled className="rounded-none"><Clock className="mr-2 h-3 w-3" />Pending</Button>
                ) : (
                  <Button data-testid="profile-friend-btn" onClick={sendFriendRequest} variant="outline" className="rounded-none">
                    <UserPlus className="mr-2 h-3 w-3" />Add friend
                  </Button>
                )}
              </>
            ) : (
              <Link to="/login"><Button variant="outline" className="rounded-none">Sign in to message</Button></Link>
            )}
          </div>

          {/* Tier progress */}
          {nextAt && (
            <div className="mt-6 max-w-lg">
              <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-muted-foreground">
                <span>{tierMeta ? "Next tier" : "First tier"}</span>
                <span className="text-primary" data-testid="profile-tier-progress-text">
                  {stats.total_upvotes} / {nextAt} upvotes
                </span>
              </div>
              <div className="mt-2 h-1 w-full bg-border overflow-hidden">
                <div className="h-full bg-primary transition-all duration-500" style={{ width: `${progress}%` }} data-testid="profile-tier-progress-bar" />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="mt-10 grid gap-px bg-border sm:grid-cols-2 lg:grid-cols-6 border border-border">
        <Stat icon={ThumbsUp} v={stats.total_upvotes} label="Upvotes" testId="stat-upvotes" />
        <Stat icon={Sparkles} v={stats.proposal_count} label="Proposals" testId="stat-proposals" />
        <Stat icon={CheckCircle2} v={stats.filtered_count} label="Filtered" testId="stat-filtered" />
        <Stat icon={Award} v={stats.implemented_count} label="Implemented" testId="stat-implemented" />
        <Stat icon={Leaf} v={`${stats.impact_co2_kg}`} unit="kg CO₂" label="Saved" testId="stat-co2" />
        <Stat icon={PlayCircle} v={stats.video_count} label="Videos" testId="stat-videos" />
      </div>

      {/* Tabs */}
      <Tabs defaultValue="proposals" className="mt-10">
        <TabsList className="rounded-none border border-border bg-transparent p-0">
          <TabsTrigger value="proposals" data-testid="tab-proposals" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Proposals</TabsTrigger>
          {videos.length > 0 && <TabsTrigger value="videos" data-testid="tab-videos" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Videos</TabsTrigger>}
          <TabsTrigger value="verified" data-testid="tab-verified" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Verified successes</TabsTrigger>
        </TabsList>

        <TabsContent value="proposals" className="mt-6">
          {proposals.length === 0 ? (
            <div className="border border-dashed border-border p-12 text-center text-muted-foreground">No proposals yet.</div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              {proposals.map((p) => (
                <Link key={p.id} to={`/proposals/${p.id}`} data-testid={`profile-proposal-${p.id}`} className="border border-border p-5 hover:border-primary transition-colors">
                  <div className="flex items-center justify-between text-[10px] uppercase tracking-widest">
                    <span className={`${p.status === "implemented" ? "text-primary" : "text-muted-foreground"}`}>{p.status}</span>
                    <span className="mono text-primary flex items-center gap-1"><ThumbsUp className="h-3 w-3" />{p.votes}</span>
                  </div>
                  <div className="mt-2 font-bold tracking-tight line-clamp-2">{p.title}</div>
                  <div className="mt-1 text-xs text-muted-foreground line-clamp-2">{p.idea}</div>
                </Link>
              ))}
            </div>
          )}
        </TabsContent>

        {videos.length > 0 && (
          <TabsContent value="videos" className="mt-6">
            <div className="grid gap-4 md:grid-cols-3">
              {videos.map((v) => (
                <div key={v.id} className="border border-border">
                  <div className="relative aspect-video bg-muted">
                    {v.thumbnail_url ? <img src={v.thumbnail_url} alt="" className="h-full w-full object-cover" /> : <div className="h-full w-full bg-gradient-to-br from-secondary to-muted" />}
                    <div className="absolute inset-0 flex items-center justify-center bg-background/40">
                      <PlayCircle className="h-8 w-8 text-primary" />
                    </div>
                  </div>
                  <div className="p-3">
                    <div className="font-bold text-sm line-clamp-2">{v.title}</div>
                    <div className="mt-1 text-[10px] uppercase tracking-widest text-muted-foreground">{v.kind} · {v.views} views</div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>
        )}

        <TabsContent value="verified" className="mt-6">
          {(() => {
            const verified = proposals.filter((p) => p.status === "implemented");
            return verified.length === 0 ? (
              <div className="border border-dashed border-border p-12 text-center text-muted-foreground">No verified successes yet.</div>
            ) : (
              <div className="grid gap-4 md:grid-cols-2">
                {verified.map((p) => (
                  <Link key={p.id} to={`/proposals/${p.id}`} className="border border-primary bg-primary/5 p-5 hover:bg-primary/10 transition-colors">
                    <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-primary"><span>Implemented by</span><span className="mono">{p.implemented_by_company || p.implemented_by}</span></div>
                    <div className="mt-2 font-bold tracking-tight line-clamp-2">{p.title}</div>
                    {(p.co2_saved_kg || p.units_deployed) && (
                      <div className="mt-3 flex items-center gap-4 text-xs text-muted-foreground">
                        {p.co2_saved_kg != null && <span><Leaf className="inline h-3 w-3 text-primary" /> {p.co2_saved_kg} kg CO₂</span>}
                        {p.units_deployed != null && <span>{p.units_deployed} units</span>}
                      </div>
                    )}
                  </Link>
                ))}
              </div>
            );
          })()}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Stat({ icon: Icon, v, unit, label, testId }) {
  return (
    <div className="bg-background p-5" data-testid={testId}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted-foreground">
        <Icon className="h-3 w-3 text-primary" />{label}
      </div>
      <div className="mt-2 mono text-2xl text-primary font-bold">
        {v}{unit && <span className="text-xs ml-1 text-muted-foreground">{unit}</span>}
      </div>
    </div>
  );
}
